import { useLayoutEffect, useMemo, useRef } from 'react'
import * as THREE from 'three'
import { useGLTF } from '@react-three/drei'
import { useThree } from '@react-three/fiber'
import { DRACO_PAD, maakLoaderUitbreiding } from './laden'
import type { MateriaalCorrectie, ProductConfig } from './types'

export interface Afmeting {
  /** Straal van de omhullende bol, na schaal. Bepaalt camera-afstand en zoomgrenzen. */
  straal: number
  hoogte: number
  midden: THREE.Vector3
}

/**
 * Het geladen GLB-model, gecentreerd en opgemeten.
 *
 * Hier staat geen geometrie. Wat hier wél gebeurt is het handvol dingen dat een
 * geëxporteerd bestand bijna nooit goed meelevert en dat wel bepaalt of het als
 * product of als game-object leest:
 *
 * 1. **Centreren op de oorsprong en op de vloer zetten.** Exporteurs leggen het
 *    nulpunt overal neer. Zonder dit draait het model om een punt buiten zichzelf.
 * 2. **Opmeten.** De viewer leidt camera-afstand, zoomgrenzen en schaduwgrootte
 *    af uit de werkelijke maat, zodat één config voor een pallettruck van 2 m en
 *    een reachtruck van 8 m werkt.
 * 3. **Materiaalcorrecties per naam.** Image-to-3D en CAD-conversies leveren
 *    vrijwel altijd banden die spiegelen als lak en metaal dat te schoon is. Dat
 *    is de hoofdoorzaak van de plastic uitstraling. De correcties staan als data
 *    in de catalogus, niet hier.
 * 4. **envMapIntensity zetten.** Zonder dat doet de studio-omgeving niets en
 *    blijft elk materiaal dof.
 */
export function ProductModel({
  product, onGemeten,
}: {
  product: ProductConfig & { modelUrl: string }
  onGemeten?: (a: Afmeting) => void
}) {
  const renderer = useThree((s) => s.gl)
  const uitbreiding = useMemo(() => maakLoaderUitbreiding(renderer), [renderer])
  const { scene } = useGLTF(product.modelUrl, DRACO_PAD, true, uitbreiding)
  const groep = useRef<THREE.Group>(null)

  // De meldcallback in een ref in plaats van in de afhankelijkheden hieronder.
  // Anders: elke render van de ouder geeft een nieuwe functie-identiteit, dat
  // laat het effect opnieuw lopen, dat zet state, dat rendert de ouder opnieuw —
  // een lus die het beeld zwart houdt zonder ook maar één foutmelding.
  const melden = useRef(onGemeten)
  melden.current = onGemeten

  // Elke viewer krijgt een eigen kopie: useGLTF deelt de scene tussen
  // aanroepen, en twee viewers die hetzelfde object transformeren vechten.
  const object = useMemo(() => {
    const kopie = scene.clone(true)
    kopie.traverse((o) => {
      if (!(o instanceof THREE.Mesh)) return
      o.castShadow = true
      o.receiveShadow = true
      o.frustumCulled = true
      // De vorm behouden: een mesh met één materiaal moet één materiaal
      // terugkrijgen, geen array van één. Three.js leest een array als
      // multi-material en zoekt dan geometry groups die er niet zijn — het
      // resultaat is een mesh die wél in de scène staat maar niets tekent.
      o.material = Array.isArray(o.material)
        ? o.material.map((m) => bewerkMateriaal(m, product.materialen, renderer))
        : bewerkMateriaal(o.material, product.materialen, renderer)
    })
    return kopie
  }, [scene, product.materialen, renderer])

  useLayoutEffect(() => {
    const g = groep.current
    if (!g) return

    // Eerst de transformatie uit de config, dan pas meten: anders meet je het
    // bestand en niet wat er op het scherm staat.
    g.scale.setScalar(product.schaal)
    g.rotation.set(...product.rotatie)
    g.position.set(0, 0, 0)
    g.updateWorldMatrix(true, true)

    const doos = new THREE.Box3().setFromObject(g)
    const maat = doos.getSize(new THREE.Vector3())
    const midden = doos.getCenter(new THREE.Vector3())

    // Op de vloer, gecentreerd op x en z. Het draaipunt komt zo in het hart van
    // het product te liggen in plaats van in een willekeurige hoek.
    g.position.set(-midden.x, -doos.min.y, -midden.z)

    const bol = doos.getBoundingSphere(new THREE.Sphere())
    melden.current?.({
      straal: bol.radius,
      hoogte: maat.y,
      midden: new THREE.Vector3(0, maat.y / 2, 0),
    })
  }, [object, product.schaal, product.rotatie])

  return <group ref={groep}><primitive object={object} /></group>
}

/**
 * Past de correcties uit de catalogus toe en zet de basiswaarden die een
 * geëxporteerd materiaal doorgaans mist.
 */
function bewerkMateriaal(
  bron: THREE.Material,
  correcties: MateriaalCorrectie[] | undefined,
  renderer: THREE.WebGLRenderer,
): THREE.Material {
  if (!(bron instanceof THREE.MeshStandardMaterial)) return bron

  const c = correcties?.find((k) =>
    k.namen.some((n) => bron.name.toLowerCase().includes(n.toLowerCase())),
  )

  // Vraagt de correctie om lak, dan moet het een physical material worden:
  // clearcoat bestaat niet op MeshStandardMaterial.
  const wilLak = (c?.lak ?? 0) > 0
  const m = wilLak && !(bron instanceof THREE.MeshPhysicalMaterial)
    ? nieuwPhysicalUit(bron)
    : (bron.clone() as THREE.MeshStandardMaterial)

  if (c?.ruwheid !== undefined) m.roughness = c.ruwheid
  if (c?.metaal !== undefined) m.metalness = c.metaal
  m.envMapIntensity = c?.omgeving ?? 1
  if (m instanceof THREE.MeshPhysicalMaterial && c) {
    m.clearcoat = c.lak ?? 0
    m.clearcoatRoughness = c.lakRuwheid ?? 0.1
  }

  // Texturen: juiste kleurruimte en scherpte onder een hoek. Zonder anisotropie
  // vervaagt een sticker op een schuin paneel tot een grijze vlek.
  const max = renderer.capabilities.getMaxAnisotropy()
  for (const sleutel of ['map', 'emissiveMap'] as const) {
    const t = m[sleutel]
    if (t) { t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = Math.min(8, max) }
  }
  for (const sleutel of ['normalMap', 'roughnessMap', 'metalnessMap', 'aoMap'] as const) {
    const t = m[sleutel]
    if (t) { t.colorSpace = THREE.NoColorSpace; t.anisotropy = Math.min(8, max) }
  }

  m.needsUpdate = true
  return m
}

/** Zet een standaardmateriaal om naar physical met behoud van alles wat erop zat. */
function nieuwPhysicalUit(bron: THREE.MeshStandardMaterial): THREE.MeshPhysicalMaterial {
  const m = new THREE.MeshPhysicalMaterial()
  THREE.Material.prototype.copy.call(m, bron)
  m.color.copy(bron.color)
  m.roughness = bron.roughness
  m.metalness = bron.metalness
  m.map = bron.map
  m.normalMap = bron.normalMap
  m.normalScale.copy(bron.normalScale)
  m.roughnessMap = bron.roughnessMap
  m.metalnessMap = bron.metalnessMap
  m.aoMap = bron.aoMap
  m.aoMapIntensity = bron.aoMapIntensity
  m.emissive.copy(bron.emissive)
  m.emissiveMap = bron.emissiveMap
  m.emissiveIntensity = bron.emissiveIntensity
  m.name = bron.name
  return m
}
